const WAITLIST_STATUS = 'waitlist'

export const WAITLIST_SCHOOL_NAMES = {
  mma: 'Mountainside',
  lsma: 'Lakeside',
  tma: 'Treeside',
  mor: 'Morningside',
  psma: 'Parkside',
  den: 'Denton',
  tem: 'Tempe',
  tul: 'Tulsa',
}

const LAKESIDE_TWIN_CITIES_ID = 'lsma-twin-cities'
const LAKESIDE_DULUTH_ID = 'lsma-duluth'
const LAKESIDE_REVIEW_ID = 'lsma-location-review'

const DULUTH_AREA_TOWNS = new Set([
  'adolph',
  'arnold',
  'brule',
  'brookston',
  'carlton',
  'cloquet',
  'duluth',
  'duluth township',
  'esko',
  'foxboro',
  'french river',
  'hermantown',
  'knife river',
  'lake nebagamon',
  'maple',
  'oliver',
  'poplar',
  'proctor',
  'saginaw',
  'scanlon',
  'solon springs',
  'south range',
  'superior',
  'two harbors',
  'twig',
  'wrenshall',
])

const SCHOOL_ALIASES = {
  mountainside: 'mma',
  'mountainside music academy': 'mma',
  lakeside: 'lsma',
  'lakeside music academy': 'lsma',
  treeside: 'tma',
  'treeside music academy': 'tma',
  tsma: 'tma',
  morningside: 'mor',
  'morningside music academy': 'mor',
  parkside: 'psma',
  'parkside music academy': 'psma',
  denton: 'den',
  'denton music academy': 'den',
  tempe: 'tem',
  'tempe music academy': 'tem',
  tulsa: 'tul',
  'tulsa music academy': 'tul',
}

const normalize = (value) => String(value || '').trim().toLowerCase()

const normalizeTown = (value) => normalize(value)
  .replace(/\b(city|township|village) of\b/g, '')
  .replace(/[^a-z\d\s-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

export const normalizeWaitlistSchoolId = (value) => {
  const normalized = normalize(value)
  return SCHOOL_ALIASES[normalized] || normalized
}

const isWaitlisted = (value) => normalize(value) === WAITLIST_STATUS

const looksLikeDatabaseId = (value) => /^[a-f\d]{24}$/i.test(String(value || '').trim())

const getSchoolId = (school) => normalizeWaitlistSchoolId(
  school?.value || school?.abbreviation || school?.school || school?.id
)

const getSchoolName = (school, schoolId) => String(
  school?.label || school?.name || school?.schoolName || WAITLIST_SCHOOL_NAMES[schoolId] || schoolId.toUpperCase()
).trim()

const getFamilyLocation = (family) => {
  const address = family?.address || family?.familyAddress || family?.Address || {}
  return {
    city: String(address?.city || address?.town || family?.city || family?.town || '').trim(),
    state: String(
      address?.state
      || address?.province?.code
      || address?.province
      || family?.state
      || ''
    ).trim(),
  }
}

export const getLakesideWaitlistArea = (city) => {
  const normalizedCity = normalizeTown(city)
  if (!normalizedCity) return 'review'
  return DULUTH_AREA_TOWNS.has(normalizedCity) ? 'duluth' : 'twin-cities'
}

const getReportingSchoolId = (schoolId, city) => {
  if (schoolId !== 'lsma') return schoolId

  const lakesideArea = getLakesideWaitlistArea(city)
  if (lakesideArea === 'duluth') return LAKESIDE_DULUTH_ID
  if (lakesideArea === 'review') return LAKESIDE_REVIEW_ID
  return LAKESIDE_TWIN_CITIES_ID
}

const addSchoolDetail = (schoolDetails, schoolId, schoolName) => {
  if (schoolId === 'lsma') {
    schoolDetails.set(LAKESIDE_TWIN_CITIES_ID, {
      id: LAKESIDE_TWIN_CITIES_ID,
      baseSchoolId: 'lsma',
      code: 'LSMA · TWIN CITIES',
      name: 'Lakeside — Twin Cities',
    })
    schoolDetails.set(LAKESIDE_DULUTH_ID, {
      id: LAKESIDE_DULUTH_ID,
      baseSchoolId: 'lsma',
      code: 'LSMA · DULUTH',
      name: 'Lakeside — Duluth Area',
    })
    return
  }

  schoolDetails.set(schoolId, {
    id: schoolId,
    baseSchoolId: schoolId,
    code: schoolId.toUpperCase(),
    name: schoolName,
  })
}

const buildInstrumentDirectory = (instruments, families) => {
  const directory = new Map()

  const addInstrument = (instrument) => {
    if (!instrument || typeof instrument !== 'object') return
    const id = String(instrument.id || instrument._id || '').trim()
    const name = String(instrument.name || instrument.instrumentName || '').trim()
    if (id && name) directory.set(id, name)
  }

  ;(Array.isArray(instruments) ? instruments : []).forEach(addInstrument)
  ;(Array.isArray(families) ? families : []).forEach((family) => {
    ;(Array.isArray(family?.students) ? family.students : []).forEach((student) => {
      ;(Array.isArray(student?.instrumentList) ? student.instrumentList : []).forEach(addInstrument)
    })
  })

  return directory
}

const getInstrumentNames = (student, instrumentDirectory) => {
  const instrumentNames = (Array.isArray(student?.instrumentList) ? student.instrumentList : [])
    .map((instrument) => {
      if (instrument && typeof instrument === 'object') {
        const id = String(instrument.id || instrument._id || '').trim()
        return String(instrument.name || instrument.instrumentName || '').trim()
          || instrumentDirectory.get(id)
          || ''
      }

      const value = String(instrument || '').trim()
      return instrumentDirectory.get(value) || (looksLikeDatabaseId(value) ? '' : value)
    })
    .filter(Boolean)

  const legacyInstrument = String(student?.instrument || '').trim()
  if (instrumentNames.length === 0 && legacyInstrument) {
    const resolvedLegacyName = instrumentDirectory.get(legacyInstrument)
      || (looksLikeDatabaseId(legacyInstrument) ? '' : legacyInstrument)
    if (resolvedLegacyName) instrumentNames.push(resolvedLegacyName)
  }

  const uniqueNames = new Map()
  instrumentNames.forEach((name) => {
    const normalizedName = normalize(name)
    if (normalizedName && !uniqueNames.has(normalizedName)) {
      uniqueNames.set(normalizedName, name)
    }
  })

  return Array.from(uniqueNames.values())
}

const getStudentKey = (student, family, index) => {
  const studentId = String(student?.studentId || student?._id || '').trim()
  if (studentId) return studentId

  return [
    String(family?.familyId || family?._id || family?.todoId || '').trim(),
    normalize(student?.studentName || student?.name),
    index,
  ].join(':')
}

export const buildWaitlistSummary = ({
  families = [],
  instruments = [],
  schools = [],
} = {}) => {
  const safeFamilies = Array.isArray(families) ? families : []
  const instrumentDirectory = buildInstrumentDirectory(instruments, safeFamilies)
  const studentsByKey = new Map()

  safeFamilies.forEach((family) => {
    const familyIsWaitlisted = isWaitlisted(family?.familyStatus)
    const familyStudents = Array.isArray(family?.students) ? family.students : []

    familyStudents.forEach((student, index) => {
      if (!familyIsWaitlisted && !isWaitlisted(student?.studentStatus)) return

      const baseSchoolId = normalizeWaitlistSchoolId(
        family?.school || family?.whichSchool || student?.school
      )
      if (!baseSchoolId) return

      const location = getFamilyLocation(family)
      const schoolId = getReportingSchoolId(baseSchoolId, location.city)

      const studentKey = getStudentKey(student, family, index)
      const instrumentNames = getInstrumentNames(student, instrumentDirectory)
      const existingStudent = studentsByKey.get(studentKey)

      if (existingStudent) {
        const mergedNames = new Map(
          [...existingStudent.instrumentNames, ...instrumentNames]
            .map((name) => [normalize(name), name])
        )
        existingStudent.instrumentNames = Array.from(mergedNames.values())
        return
      }

      studentsByKey.set(studentKey, {
        studentId: String(student?.studentId || student?._id || studentKey),
        studentName: String(student?.studentName || student?.name || 'Unnamed student').trim(),
        familyId: String(family?.familyId || family?._id || family?.todoId || '').trim(),
        familyName: String(family?.familyName || family?.name || '').trim(),
        schoolId,
        baseSchoolId,
        city: location.city,
        state: location.state,
        familyAddress: family?.address || family?.familyAddress || family?.Address || null,
        familyLocation: family?.location
          || family?.coordinates
          || family?.familyLocation
          || null,
        instrumentNames,
        waitlistScope: familyIsWaitlisted ? 'family' : 'student',
      })
    })
  })

  const students = Array.from(studentsByKey.values())
  const schoolDetails = new Map()

  ;(Array.isArray(schools) ? schools : []).forEach((school) => {
    const schoolId = getSchoolId(school)
    if (!schoolId || schoolId === 'all') return
    addSchoolDetail(schoolDetails, schoolId, getSchoolName(school, schoolId))
  })

  students.forEach((student) => {
    if (!schoolDetails.has(student.schoolId)) {
      if (student.schoolId === LAKESIDE_REVIEW_ID) {
        schoolDetails.set(LAKESIDE_REVIEW_ID, {
          id: LAKESIDE_REVIEW_ID,
          baseSchoolId: 'lsma',
          code: 'LSMA · REVIEW',
          name: 'Lakeside — Location needs review',
        })
      } else {
        addSchoolDetail(
          schoolDetails,
          student.baseSchoolId,
          WAITLIST_SCHOOL_NAMES[student.baseSchoolId] || student.baseSchoolId.toUpperCase()
        )
      }
    }
  })

  const schoolSummaries = Array.from(schoolDetails.values())
    .map((school) => {
      const schoolStudents = students
        .filter((student) => student.schoolId === school.id)
        .sort((a, b) => (
          a.studentName.localeCompare(b.studentName)
          || a.familyName.localeCompare(b.familyName)
        ))
      const instrumentsByName = new Map()

      schoolStudents.forEach((student) => {
        const names = student.instrumentNames.length > 0
          ? student.instrumentNames
          : ['Instrument not listed']

        names.forEach((instrumentName) => {
          const instrumentKey = normalize(instrumentName)
          if (!instrumentsByName.has(instrumentKey)) {
            instrumentsByName.set(instrumentKey, {
              name: instrumentName,
              students: [],
            })
          }
          instrumentsByName.get(instrumentKey).students.push(student)
        })
      })

      const instrumentSummaries = Array.from(instrumentsByName.values())
        .map((instrument) => ({
          ...instrument,
          count: instrument.students.length,
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))

      return {
        ...school,
        totalStudents: schoolStudents.length,
        totalFamilies: new Set(schoolStudents.map((student) => student.familyId).filter(Boolean)).size,
        students: schoolStudents,
        instruments: instrumentSummaries,
      }
    })
    .sort((a, b) => (
      b.totalStudents - a.totalStudents
      || a.name.localeCompare(b.name)
    ))

  return {
    totalStudents: students.length,
    totalFamilies: new Set(students.map((student) => student.familyId).filter(Boolean)).size,
    schoolsWithStudents: schoolSummaries.filter((school) => school.totalStudents > 0).length,
    totalInstrumentPlacements: schoolSummaries.reduce(
      (total, school) => total + school.instruments.reduce(
        (schoolTotal, instrument) => schoolTotal + instrument.count,
        0
      ),
      0
    ),
    schools: schoolSummaries,
  }
}
